import React from 'react';
import {
  IconButton,
  makeStyles,
  createStyles,
  Theme,
  Popover,
} from '@material-ui/core';
import * as Colors from '../../../theme/colors';
import { IconType, IconNameToComponent, PickerIcons } from '../../Icons';

interface IconPickerProps {
  icon?: IconType;
  onIconSelected: (icon: string) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    iconPickerButtonContainer: {
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${Colors.NonHoverBorder}`,
    },
    iconButton: {
      fontSize: 40,
      '& > path': {
        strokeWidth: 1.25,
      },
    },
    selectIconContainer: {
      padding: theme.spacing(1),
      margin: theme.spacing(0, 0.25),
      '&:hover': {
        backgroundColor: Colors.HoverNonBorderBackground,
      },
    },
    selectIcon: {
      fontSize: 28,
      '& > path': {
        strokeWidth: 1.25,
      },
    },
    iconListContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      padding: theme.spacing(1),
      maxHeight: 400,
      maxWidth: 400, // set based on margin and size of icon buttons to fit 8 in a row
      boxSizing: 'border-box',
    },
  }),
);

export const IconPicker: React.FC<IconPickerProps> = ({
  icon = 'dashboard',
  onIconSelected,
}) => {
  const classes = useStyles();
  const IconComponent = IconNameToComponent[icon];

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const handlePickIconClicked = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <div>
      <IconButton
        onClick={handlePickIconClicked}
        className={classes.iconPickerButtonContainer}
      >
        <IconComponent className={classes.iconButton} />
      </IconButton>
      <Popover
        id="icon-picker"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div className={classes.iconListContainer}>
          {Object.entries(PickerIcons).map(([iconName, IconPickComponent]) => (
            <IconButton
              key={iconName}
              className={classes.selectIconContainer}
              onClick={() => {
                onIconSelected(iconName);
                handleClose();
              }}
            >
              <IconPickComponent className={classes.selectIcon} />
            </IconButton>
          ))}
        </div>
      </Popover>
    </div>
  );
};
